import Schema from "@components/Schema";
import SEOHEAD from "@components/SEOHEAD";
import Aside from "@components/ui/details/Asid";
import Comments from "@components/ui/details/comments";
import Courses from "@components/ui/details/courses";
import Description from "@components/ui/details/Description";
import FAQ from "@components/ui/details/FAQ";
import Mentors from "@components/ui/details/mentors";
import Prerequisites from "@components/ui/details/Prerequisites";
import Teachers from "@components/ui/details/teachers";
import Title from "@components/ui/details/Title";
import Video from "@components/ui/details/video";
import NewCourses from "@components/ui/landing/NewCourses";
import gate from "gate";
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import { useRouter } from "next/router";
import React, { FC, useEffect, useRef } from "react";
import { dehydrate, QueryClient, useQuery } from "react-query";
import { extractNumericIdFromCatchAll, sortArrayByOrder } from "src/utils";

export const getServerSideProps: GetServerSideProps = async (
  ctx: GetServerSidePropsContext
) => {
  const queryClient = new QueryClient();
  const id = extractNumericIdFromCatchAll(ctx?.params?.id);
  if (!id) {
    return { notFound: true };
  }
  let isError = false;

  try {
    await queryClient.fetchQuery(["details", id], () => gate.detail(id));
  } catch (error: any) {
    isError = true;
    const statusCode = error?.response?.status;
    const errCode = error?.code;
    const isNetworkError =
      !error?.response || ["EAI_AGAIN", "ENOTFOUND"].includes(errCode);

    ctx.res.statusCode = isNetworkError ? 503 : statusCode || 500;
    console.log("---------------->", error);
    return {
      props: {
        isError,
        dehydratedState: dehydrate(queryClient),
      },
    };
  }

  return {
    props: {
      isError,
      dehydratedState: dehydrate(queryClient),
    },
  };
};

type Props = {
  isError: boolean;
};

// export const getServerSideProps: GetServerSideProps = async (ctx: any) => {
//     let data;
//     try {
//         const res: any = await gate.detail(ctx?.query?.id[0]);
//         data = res;
//     } catch (error) {
//         console.log(error);
//         return {
//             notFound: true,
//         };
//     }
//     return {
//         props: {
//             data: data?.data?.courseDetail,
//             id: ctx?.query?.id[0],
//         },
//     };
// };

// type Props = {
//     id: number;
//     data: any;
// };

const Index: FC<Props> = ({ isError }) => {
  const { query }: any = useRouter();
  const id = extractNumericIdFromCatchAll(query?.id);
  const prerequisitesRef: React.LegacyRef<HTMLDivElement> | any = useRef(null);
  const { data, status } = useQuery(["details", id], () => gate.detail(id), {
    select: (data: any) => data?.data?.courseDetail,
    enabled: !!id,
  });

  useEffect(() => {
    console.log("[course] raw query.id:", query?.id, "extracted id:", id);
  }, [query?.id, id]);
  useEffect(() => {
    if (query.campaign_id && query.phone_number) {
      localStorage.setItem(
        "campaign",
        JSON.stringify({
          campaign_id: query.campaign_id,
          followup_id: query.followup_id,
          phone_number: query.phone_number,
          url: window.location.origin + window.location.pathname,
          isPromotion: query?.isPromotion,
        })
      );
    }
  }, []);
  return (
    <>
      <SEOHEAD
        {...{
          canonical: `course/${id}/${data?.seoUrlDescription}`,
          keyword: data?.seoKeyword,
          seoDescription: data?.seoDescription,
          title: data?.title ?? "دوره های دادبانان",
          image: data?.image,
        }}
      />
      <Schema
        {...{
          title: data?.name,
          keyword: data?.seoKeyword,
          seoDescription: data?.seoDescription,
          description: data?.shortDescription,
          featuredImage: data?.image,
          date: data?.createdDate,
          price: data?.price,
          commentCount: data?.comments?.length,
          ratingCount: 5,
          canonical: `course/${id}/${data?.seoUrlDescription}`,
        }}
      />

      <div className="relative h-full bg-[#FAFAFC]">
        <div className="relative mb-7 max-h-[440px] w-full">
          <img
            src="/images/detail-bg.jpg"
            loading="lazy"
            className="max-h-[440px] w-full"
            alt="بنر"
          />
          <Title
            type="DESKTOP"
            data={{ name: data?.name, description: data?.shortDescription }}
          />
        </div>
        <div className="relative -top-20 md:-top-32 lg:static lg:top-0 lg:flex">
          <div className="flex flex-col items-end px-6 lg:ml-5 lg:w-[37%] lg:px-0">
            <Aside
              {...{
                titleData: {
                  name: data?.name,
                  description: data?.shortDescription,
                },
                prerequisitesRef,
                imageAlternateText: data?.imageAlternateText,
                image: data?.image,
                isBuyable: data?.isBuyable,
                badge: data?.badge,
                price: data?.price,
                holdingType: data?.holdingType,
                discount: data?.discount,
                extraDescription: data?.extraDescription,
                video: {
                  name: data?.summaryVideoName,
                  description: data?.shortDescription,
                  date: data?.startDate ?? data?.createdDate,
                  url: data?.summaryVideo,
                  isSchema: !!data?.summaryVideo,
                },
                date: {
                  start: data?.startDate,
                  end: data?.endDate,
                  releaseDate: data?.releaseDatetime,
                },
                courseDuration: data?.courseDuration,
                certificate: {
                  certificateTitle: data?.certificateTitle,
                  hasCertificate: data?.hasCertificate,
                },
                courseType: data?.courseType,
                isFree: data?.isFree,
                releaseDate: data?.releaseDate,
                id: data?.id,
                allowSnapPay: data?.allowSnapPay,
                snappPayInstallment: data?.snappPayInstallment,
              }}
            />
          </div>
          <div className="min-h-[120vh] lg:w-[63%]">
            <div className="mb-5 space-y-5 lg:w-[78%]">
              {data?.fullDescription && (
                <div className="px-6 lg:px-0">
                  <Description
                    fullDescription={data?.fullDescription}
                    title={data?.name}
                  />
                </div>
              )}
              {!!data?.courseSessions?.length && (
                <div className="px-6 lg:px-0">
                  <Courses data={data?.courseSessions} />
                </div>
              )}
              {!!data?.teachers?.length && (
                <div className="px-6 lg:px-0">
                  <Teachers data={data?.teachers} />
                </div>
              )}
              {!!data?.mentors?.length && (
                <div className="px-6 lg:px-0">
                  <Mentors mentors={data?.mentors} />
                </div>
              )}
              {!!data?.video && (
                <div className="px-0 md:px-6 lg:px-0">
                  <Video
                    {...{
                      url: data?.video,
                      name: data?.videoName,
                      schemaName: data?.name,
                      description: data?.shortDescription,
                      date: data?.startDate ?? data?.createdDate,
                      isSchema: !!data?.video,
                    }}
                  />
                </div>
              )}
              <input hidden ref={prerequisitesRef} />
              {!!data?.prerequisiteCourses?.length && (
                <div className="px-6 lg:px-0" ref={prerequisitesRef}>
                  <Prerequisites data={data?.prerequisiteCourses} />
                </div>
              )}
              {!!data?.frequentlyAskedQuestions?.length && (
                <div className="px-6 lg:px-0">
                  <FAQ
                    frequentlyAskedQuestions={
                      sortArrayByOrder(
                        data?.frequentlyAskedQuestions,
                        "order"
                      ) as any
                    }
                  />
                </div>
              )}
              {data?.comments && (
                <div className="px-6 lg:px-0">
                  <Comments
                    id={id as any}
                    type="course"
                    comments={data?.comments}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {data?.recommendedCourses?.length !== 0 && (
          <div className="my-4 flex w-full gap-x-2 px-[10%] md:mt-10 lg:my-16 lg:px-[10%]">
            <NewCourses
              title="دوره ها و بسته های پیشنهادی"
              data={data?.recommendedCourses}
              breakpoints={{
                300: {
                  slidesPerView: 1,
                  spaceBetween: 5,
                },
                375: {
                  slidesPerView: 1.1,
                  spaceBetween: 5,
                },
                767: {
                  slidesPerView: 2,
                  spaceBetween: 20,
                },
                1024: {
                  slidesPerView: 4,
                  spaceBetween: 24,
                },
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Index;
